import {
  Checkbox,
  FormControlLabel,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';

import { TitleContainer } from '../../components/title-container/TitleContainer';
import IPage from '../../interfaces/IPage';
import { useCallback, useEffect, useState } from 'react';
import CustomTableSortLabel from '../../components/table/CustomTableSortLabel/CustomTableSortLabel';
import { useToastr } from '../../hooks/useToastr';
import CustomTablePagination from '../../components/table/CustomTablePagination/CustomTablePagination';
import { PageCard } from '../../components/page-card/PageCard';
import { BackdropCustom } from '../../components/backdrop/Backdrop';
import { useFieldConfigurationsService } from '../../services/useFieldConfigurationsService';
import IFieldConfigurations from '../../interfaces/IFieldConfigurations';

const FieldConfigurationsList: React.FC<IPage> = ({ title }) => {
  window.document.title = title;

  const [fieldConfigurations, setFieldConfigurations] = useState<IFieldConfigurations[]>([]);
  const [totalRows, setTotalRows] = useState(0);
  const [perPage, setPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(0);
  const [orderDirection, setOrderDirection] = useState<'asc' | 'desc'>('asc');
  const [orderField, setOrderField] = useState<string>('id');
  const [loading, setLoading] = useState(false);

  const { findAll, updateFieldVisible, updateFieldRequired } = useFieldConfigurationsService();

  const toastr = useToastr();

  const handleList = useCallback(
    async (perPage: number, currentPage: number, orderField: string, orderDirection: 'asc' | 'desc') => {
      setLoading(true);
      await findAll(
        `perPage=${perPage}&currentPage=${currentPage + 1}&orderBy=${orderField}&orderDirection=${orderDirection}`,
      )
        .then(response => {
          const imageParams = response.data;

          if (imageParams.length > 0) {
            setFieldConfigurations(response.data);
            setTotalRows(response.totalRows);
            setLoading(false);
          } else {
            setFieldConfigurations([]);
            setTotalRows(0);
            setPerPage(10);
            setCurrentPage(0);
            setLoading(false);
          }
        })
        .catch(error => {
          toastr.error(error?.message || 'Contate a equipe de suporte');
        })
        .finally(() => {
          setLoading(false);
        });
    },
    [],
  );

  useEffect(() => {
    handleList(perPage, currentPage, orderField, orderDirection);
  }, [perPage, currentPage, orderField, orderDirection, handleList]);

  return (
    <>
      <TitleContainer>
        <h1>Configurações dos Campos</h1>
      </TitleContainer>
      <PageCard>
        <TableContainer sx={{ marginTop: '2px', paddingLeft: '8px', paddingRight: '8px' }}>
          {loading && <BackdropCustom />}
          <Table>
            <TableHead>
              <TableRow
                sx={{
                  '&:last-child td, &:last-child th': {
                    borderBottom: 1,
                    borderColor: '#E5E5E5',
                  },
                }}
              >
                <CustomTableSortLabel
                  perPage={perPage}
                  currentPage={currentPage}
                  orderField={orderField}
                  field="fieldName"
                  label="Nome do Campo"
                  handleRequest={handleList}
                  orderDirection={orderDirection}
                  setField={setOrderField}
                  setDirection={setOrderDirection}
                />
                <CustomTableSortLabel
                  perPage={perPage}
                  currentPage={currentPage}
                  orderField={orderField}
                  field="fieldDescription"
                  label="Descrição do Campo"
                  handleRequest={handleList}
                  orderDirection={orderDirection}
                  disableOrder
                  setField={setOrderField}
                  setDirection={setOrderDirection}
                />
                <CustomTableSortLabel
                  perPage={perPage}
                  currentPage={currentPage}
                  orderField={orderField}
                  field="fieldVisible"
                  label="Campo vísivel"
                  handleRequest={handleList}
                  orderDirection={orderDirection}
                  disableOrder
                  setField={setOrderField}
                  setDirection={setOrderDirection}
                />
                <CustomTableSortLabel
                  perPage={perPage}
                  currentPage={currentPage}
                  orderField={orderField}
                  field="fieldRequired"
                  label="Campo Obrigatório"
                  handleRequest={handleList}
                  orderDirection={orderDirection}
                  disableOrder
                  setField={setOrderField}
                  setDirection={setOrderDirection}
                />
              </TableRow>
            </TableHead>
            <TableBody>
              {fieldConfigurations &&
                fieldConfigurations.map(fieldConfiguration => {
                  return (
                    <TableRow
                      key={fieldConfiguration.id}
                      sx={{
                        '&:last-child td, &:last-child th': {
                          border: 0,
                        },
                      }}
                    >
                      <TableCell size="small">{fieldConfiguration?.fieldName}</TableCell>
                      <TableCell size="small">{fieldConfiguration?.fieldDescription}</TableCell>
                      <TableCell size="small">
                        <FormControlLabel
                          label=""
                          control={
                            <Checkbox
                              onChange={async e => {
                                if (fieldConfiguration.id) {
                                  const data = await updateFieldVisible({
                                    id: fieldConfiguration.id,
                                    fieldVisible: e.target.checked,
                                  });

                                  const newData = fieldConfigurations.map(field => {
                                    if (field.id === fieldConfiguration.id) {
                                      field.fieldVisible = data.fieldVisible;
                                    }

                                    return field;
                                  });

                                  setFieldConfigurations(newData);
                                }
                              }}
                              checked={Boolean(fieldConfiguration.fieldVisible)}
                            />
                          }
                        />
                      </TableCell>
                      <TableCell size="small">
                        <FormControlLabel
                          label=""
                          control={
                            <Checkbox
                              onChange={async e => {
                                if (fieldConfiguration.id) {
                                  const data = await updateFieldRequired({
                                    id: fieldConfiguration.id,
                                    fieldRequired: e.target.checked,
                                  });

                                  const newData = fieldConfigurations.map(field => {
                                    if (field.id === fieldConfiguration.id) {
                                      field.fieldRequired = data.fieldRequired;
                                    }

                                    return field;
                                  });

                                  setFieldConfigurations(newData);
                                }
                              }}
                              checked={Boolean(fieldConfiguration.fieldRequired)}
                            />
                          }
                        />
                      </TableCell>
                    </TableRow>
                  );
                })}
            </TableBody>
          </Table>
          <CustomTablePagination
            totalRows={totalRows}
            currentPage={currentPage}
            perPage={perPage}
            handleRequest={handleList}
            setPerPage={setPerPage}
            setCurrentPage={setCurrentPage}
            orderField={orderField}
            orderDirection={orderDirection}
          />
        </TableContainer>
      </PageCard>
    </>
  );
};

export { FieldConfigurationsList };
